<div class="flex flex-col flex-1 min-h-0 overflow-hidden">

  <!-- Toolbar -->
  <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">

    <!-- Breadcrumb -->
    <nav class="flex items-center gap-1 text-sm flex-1 min-w-0">
      @for (crumb of breadcrumbs; track crumb.prefix; let last = $last) {
        @if (!last) {
          <button
            (click)="navigateTo(crumb.prefix)"
            class="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[120px]">
            {{ crumb.label }}
          </button>
          <span class="text-gray-400">/</span>
        } @else {
          <span class="text-gray-700 dark:text-gray-300 font-medium truncate">{{ crumb.label }}</span>
        }
      }
    </nav>

    <!-- Action buttons -->
    <div class="flex items-center gap-2 flex-shrink-0">
      <button
        (click)="showCreateFolder = true"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        </svg>
        New Folder
      </button>
      <label class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white cursor-pointer transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
        Upload
        <input type="file" multiple class="hidden"
          accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg,.txt,.md"
          (change)="onFileInput($event)" />
      </label>
      <button
        (click)="loadFolder(currentPrefix)"
        class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Refresh">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Batch action bar (shown when files are selected) -->
  @if (selectedCount > 0) {
    <div class="flex items-center gap-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 text-sm">
      <span class="text-blue-700 dark:text-blue-300 font-medium">{{ selectedCount }} selected</span>
      <div class="flex items-center gap-2 ml-auto">
        @if (canBatchParse) {
          <button
            (click)="openBatchAction('parse')"
            class="px-3 py-1 rounded-lg text-xs font-medium border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors">
            Parse
          </button>
        }
        @if (canBatchIngest) {
          <button
            (click)="openBatchAction('ingest')"
            class="px-3 py-1 rounded-lg text-xs font-medium border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors">
            Ingest
          </button>
        }
        @if (selectedHasIngested) {
          <span
            class="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed"
            title="Ingested files cannot be moved — their path is referenced in the Knowledge Graph">
            Move
          </span>
        } @else {
          <button
            (click)="openBatchAction('move')"
            class="px-3 py-1 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            Move
          </button>
        }
        @if (selectedHasIngested) {
          <span
            class="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed"
            title="Ingested files cannot be deleted — replace them with a new version instead">
            Delete
          </span>
        } @else {
          <button
            (click)="requestDelete()"
            class="px-3 py-1 rounded-lg text-xs font-medium border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            Delete
          </button>
        }
        <button (click)="clearSelection()" class="text-gray-400 hover:text-gray-600 ml-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  }


  <!-- Drop zone overlay -->
  <div
    class="relative flex-1 overflow-y-auto"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)">

    @if (isDragging) {
      <div class="absolute inset-0 z-10 flex items-center justify-center bg-blue-500/10 border-2 border-dashed border-blue-400 rounded-lg pointer-events-none">
        <div class="text-blue-600 dark:text-blue-300 text-base font-medium">Drop files to upload</div>
      </div>
    }

    <!-- Error state -->
    @if (loadError) {
      <div class="m-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
        {{ loadError }}
      </div>
    }

    <!-- Loading state -->
    @if (loading) {
      <div class="flex items-center justify-center py-20 text-gray-400">
        <svg class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
      </div>
    }

    <!-- File listing -->
    @if (!loading) {
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <th class="w-8 px-4 py-2 text-left">
              <input
                type="checkbox"
                [checked]="allSelected"
                [indeterminate]="selectedCount > 0 && !allSelected"
                (change)="toggleSelectAll()"
                class="accent-blue-600 cursor-pointer" />
            </th>
            <th class="px-3 py-2 text-left font-medium">Name</th>
            <th class="px-3 py-2 text-right font-medium w-24">Size</th>
            <th class="px-3 py-2 text-left font-medium w-36">Modified</th>
            <th class="px-3 py-2 text-center font-medium w-72">Status</th>
          </tr>
        </thead>
        <tbody>
          <!-- Folders -->
          @for (folder of folders; track folder) {
            <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800/50 cursor-pointer group"
                (click)="navigateTo(folder)">
              <td class="px-4 py-2.5"></td>
              <td class="px-3 py-2.5" colspan="4">
                <div class="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                  <svg class="w-4 h-4 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                  </svg>
                  <span class="font-medium">{{ folderName(folder) }}</span>
                  <div class="ml-auto flex items-center gap-1">
                    <button
                      (click)="requestDeleteFolder(folder); $event.stopPropagation()"
                      class="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                      title="Delete folder">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <svg class="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </td>
            </tr>
          }

          <!-- Files -->
          @for (file of enrichedFiles; track file.key) {
            <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800/50"
                [class.bg-blue-50]="selected.has(file.key)">
              <td class="px-4 py-2.5">
                <input
                  type="checkbox"
                  [checked]="selected.has(file.key)"
                  (change)="toggleSelect(file.key)"
                  (click)="$event.stopPropagation()"
                  class="accent-blue-600 cursor-pointer" />
              </td>
              <td class="px-3 py-2.5">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span class="text-gray-800 dark:text-gray-200 truncate max-w-[280px]" [title]="fileBasename(file.key)">{{ fileBasename(file.key) }}</span>
                </div>
              </td>
              <td class="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400">{{ formatBytes(file.size) }}</td>
              <td class="px-3 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{{ file.lastModified | date: 'MMM d, y' }}</td>
              <td class="px-3 py-2.5 text-center">
                @if (statusLoading) {
                  <span class="inline-block w-16 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></span>
                } @else {
                  <div class="flex items-center justify-center gap-1">
                    @switch (file.status) {
                      @case ('INGESTED') {
                        <div class="flex flex-col items-center gap-0.5">
                          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Ingested</span>
                          @if (file.hasNewerParsed) {
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">Has Newer Parsed Version</span>
                          }
                        </div>
                      }
                      @case ('PARSED') {
                        <div class="flex flex-col items-center gap-0.5">
                          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                            [ngClass]="parsedStatusClasses(file.approvalStatus)">
                            {{ parsedStatusLabel(file.approvalStatus) }}
                          </span>
                          @if (file.approvalStatus === 'needs_review' && file.confidence !== null) {
                            <span class="text-[10px] text-gray-400 dark:text-gray-500">{{ formatConfidence(file.confidence) }}</span>
                          }
                        </div>
                      }
                      @case ('UPLOADED') {
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">Uploaded</span>
                      }
                      @default {
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">—</span>
                      }
                    }

                    <!-- Context menu trigger -->
                    <button
                      (click)="toggleMenu(file.key, $event)"
                      class="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Actions">
                      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>
                }
              </td>
            </tr>
          }

          <!-- Empty state -->
          @if (!folders.length && !enrichedFiles.length) {
            <tr>
              <td colspan="5" class="px-4 py-16 text-center text-gray-400 dark:text-gray-500 text-sm">
                <svg class="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                This folder is empty. Upload files or create a subfolder.
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>

  <!-- Upload progress bar -->
  @if (activeUpload) {
    <div class="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
      <div class="flex items-center gap-3 text-sm">
        <svg class="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
        <span class="text-gray-600 dark:text-gray-400 truncate flex-1">Uploading {{ activeUpload.filename }}…</span>
        <span class="text-gray-500 text-xs flex-shrink-0">{{ activeUpload.percent }}%</span>
      </div>
      <div class="mt-1.5 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div class="h-full bg-blue-500 rounded-full transition-all" [style.width.%]="activeUpload.percent"></div>
      </div>
    </div>
  }

  <!-- Upload error -->
  @if (uploadError) {
    <div class="mx-4 my-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400 flex items-center justify-between">
      <span>{{ uploadError }}</span>
      <button (click)="uploadError = null" class="ml-2 text-red-400 hover:text-red-600">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  }

  @if (folderDeleteError) {
    <div class="mx-4 my-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400 flex items-center justify-between">
      <span>{{ folderDeleteError }}</span>
      <button (click)="folderDeleteError = null" class="ml-2 text-red-400 hover:text-red-600">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  }

  @if (kbDeleteError) {
    <div class="mx-4 my-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400 flex items-center justify-between">
      <span>{{ kbDeleteError }}</span>
      <button (click)="kbDeleteError = null" class="ml-2 text-red-400 hover:text-red-600">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  }

  @if (kbDeleteInProgress) {
    <div class="mx-4 my-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
      <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span>Removing from knowledge base… This may take a while for large documents.</span>
    </div>
  }

</div>

<!-- Hidden input for version replacement (any filename → existing s3Key) -->
<input #versionInput type="file" class="hidden"
  accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg,.txt,.md"
  (change)="onVersionFileInput($event)" />

<!-- Dialogs -->
@if (showCreateFolder) {
  <app-create-folder-dialog
    (confirmed)="onCreateFolderConfirmed($event)"
    (cancel)="showCreateFolder = false">
  </app-create-folder-dialog>
}

@if (showDeleteConfirm) {
  <app-confirm-delete-dialog
    [count]="selectedCount"
    (confirmed)="onDeleteConfirmed()"
    (cancel)="showDeleteConfirm = false">
  </app-confirm-delete-dialog>
}

@if (folderToDelete) {
  <app-confirm-delete-dialog
    title="Delete folder"
    description="This will permanently delete the folder and all its contents from storage. This action cannot be undone."
    (confirmed)="onFolderDeleteConfirmed()"
    (cancel)="folderToDelete = null">
  </app-confirm-delete-dialog>
}

@if (menuDeleteFile) {
  <app-confirm-delete-dialog
    (confirmed)="onMenuDeleteConfirmed()"
    (cancel)="menuDeleteFile = null">
  </app-confirm-delete-dialog>
}

@if (kbDeleteFile) {
  <app-confirm-delete-dialog
    title="Delete from knowledge base"
    description="This will remove this document — including all previous versions — from the knowledge graph and vector database. Knowledge shared with other documents is preserved. The file itself stays in storage and can be re-ingested later."
    (confirmed)="onKbDeleteConfirmed()"
    (cancel)="kbDeleteFile = null">
  </app-confirm-delete-dialog>
}

@if (menuPendingAction) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
    <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
      <div class="flex items-start gap-3 mb-4">
        <div class="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            @switch (menuPendingAction.type) {
              @case ('parse') { Parse file }
              @case ('ingest') { Ingest file }
              @case ('reingest') { Re-ingest file }
            }
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{{ fileBasename(menuPendingAction.file.key) }}</p>
        </div>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
        @switch (menuPendingAction.type) {
          @case ('parse') {
            Extract text from this file. The result will be stored and ready to ingest into the Knowledge Graph.
          }
          @case ('ingest') {
            Add this file's content to the Knowledge Graph. It will be immediately available for queries.
          }
          @case ('reingest') {
            Re-ingest from the updated parsed version. The Knowledge Graph will be updated with the revised content.
          }
        }
      </p>
      <div class="flex justify-end gap-2">
        <button
          (click)="menuPendingAction = null"
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          Cancel
        </button>
        <button
          (click)="menuConfirmAction()"
          class="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors">
          Confirm
        </button>
      </div>
    </div>
  </div>
}

@if (showVersionConflict) {
  <app-version-conflict-dialog
    [conflicts]="versionConflicts"
    (replace)="onVersionConflictReplace()"
    (cancel)="onVersionConflictCancel()">
  </app-version-conflict-dialog>
}

@if (showUploadOptions && pendingFiles.length) {
  <app-upload-options-dialog
    [filename]="pendingFiles[0].name"
    (confirmed)="onUploadOptionsConfirmed($event)"
    (cancel)="onUploadOptionsCancelled()">
  </app-upload-options-dialog>
}


@if (historyFile) {
  <app-file-history-dialog
    [s3Key]="historyFile.key"
    [filename]="fileBasename(historyFile.key)"
    (close)="historyFile = null">
  </app-file-history-dialog>
}

@if (pendingBatchAction) {
  <app-batch-action-confirm-dialog
    [action]="pendingBatchAction.type"
    [count]="pendingBatchAction.keys.length"
    (confirmed)="onBatchActionConfirmed($event)"
    (cancel)="pendingBatchAction = null">
  </app-batch-action-confirm-dialog>
}

<!-- File context menu — fixed to viewport so it's never clipped by scroll containers -->
@if (openMenuKey && menuPosition) {
  <div
    (click)="$event.stopPropagation()"
    class="fixed z-50 w-56 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    [style.top.px]="menuPosition.top"
    [style.right.px]="menuPosition.right">

      <!-- Menu items -->
      @for (file of enrichedFiles; track file.key) {
        @if (file.key === openMenuKey) {

          @if (file.status === 'UPLOADED' && !isTextFile(file.key)) {
            <button (click)="menuStageAction('parse', file, $event)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Parse
            </button>
          }

          @if ((file.status === 'UPLOADED' && isTextFile(file.key)) || file.status === 'PARSED') {
            <button (click)="menuStageAction('ingest', file, $event)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Ingest
            </button>
          }

          @if (file.status === 'INGESTED') {
            @if (file.hasNewerParsed) {
              <button (click)="menuStageAction('reingest', file, $event)"
                class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Re-ingest
              </button>
            }
            <button (click)="menuUploadNewVersion(file)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload new version
            </button>
          }

          @if (file.parsedS3Key) {
            <button (click)="menuViewParsedFile(file)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View parsed file
            </button>
          }

          <button (click)="menuShowHistory(file)"
            class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Version history
          </button>

          @if (file.status === 'INGESTED') {
            <div class="border-t border-gray-100 dark:border-gray-800"></div>
            <button (click)="menuRequestKbDelete(file)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
              </svg>
              Delete from knowledge base
            </button>
          }

          @if (file.status !== 'INGESTED') {
            <div class="border-t border-gray-100 dark:border-gray-800"></div>
            <button (click)="menuRequestDelete(file)"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          }

        }
      }
  </div>
}
